// Web Search Visibility (route /geo-report).
//
// What the score is: for each workspace, Martian runs a fixed set of template
// web searches about the site through Tavily; a mention of the domain in
// Tavily's generated answer counts double and a link among the results counts
// once (server.py run_geo_check_for_client). It is a WEB SEARCH signal -- not
// AI-assistant answers (that is Share of Model / AI Answers & Mentions).
//
// Every run spends Tavily searches (Martian's internal "search credits"), so a
// run never starts from the first click: a confirmation shows the exact
// operation, its scope and the backend's own estimate (GET .../estimate).
// Brand accounts can only check one workspace they own; the bulk run across
// all workspaces is offered to agency/admin operators only -- and enforced by
// the backend, not just hidden here.
import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { geoAPI, clientsAPI, createIdempotencyKey } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { GeoRunConfirmDialog } from '../components/GeoRunConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Target, Play, CircleNotch, Warning, CheckCircle, ArrowUp, ArrowDown, Globe, ChartLine
} from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { toast } from 'sonner';

const BULK_PERSONAS = ['agency', 'admin', 'master'];

export default function GEOReportPage() {
  const { user } = useAuth();
  const canRunBulk = BULK_PERSONAS.includes(user?.tenant_type || 'brand');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  // { scope: 'workspace' | 'all_workspaces', clientId, estimate, loading, error }
  const [confirm, setConfirm] = useState(null);
  const [running, setRunning] = useState(false);
  const inFlight = useRef(false); // at most one paid request per confirmation

  useEffect(() => {
    Promise.all([clientsAPI.list(), geoAPI.report()]).then(([c, r]) => {
      setClients(c.data);
      setReport(r.data);
      if (c.data.length > 0) setSelectedClient(c.data[0].id);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    geoAPI.history(selectedClient).then(r => setHistory(r.data)).catch(() => {});
  }, [selectedClient]);

  // Opening the confirmation only reads the backend's estimate -- no search.
  const openConfirm = async (scope, clientId = null) => {
    if (!clientId && scope === 'workspace') return;
    setConfirm({ scope, clientId, idempotencyKey: createIdempotencyKey(), estimate: null, loading: true, error: null });
    try {
      const r = scope === 'workspace' ? await geoAPI.runCheckEstimate(clientId) : await geoAPI.runAllEstimate();
      setConfirm((c) => (c && c.scope === scope && c.clientId === clientId ? { ...c, estimate: r.data, loading: false } : c));
    } catch (err) {
      setConfirm((c) => (c && c.scope === scope && c.clientId === clientId ? { ...c, loading: false, error: err.response?.data?.detail || 'unavailable' } : c));
    }
  };

  const cancelConfirm = () => { if (!inFlight.current) setConfirm(null); };

  const runConfirmed = async () => {
    if (!confirm || inFlight.current) return;
    inFlight.current = true;
    setRunning(true);
    const { scope, clientId, idempotencyKey } = confirm;
    try {
      if (scope === 'workspace') {
        await geoAPI.runCheck(clientId, idempotencyKey);
        toast.success('Web search visibility check complete');
      } else {
        const res = await geoAPI.runAll(idempotencyKey);
        toast.success(`Checked ${res.data.clients_checked} workspace${res.data.clients_checked === 1 ? '' : 's'}`);
      }
      setConfirm(null);
      const [r, h] = await Promise.all([geoAPI.report(), geoAPI.history(scope === 'workspace' ? clientId : selectedClient)]);
      setReport(r.data);
      setHistory(h.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "The check couldn't be run.");
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  };

  const selectedName = (clients.find((c) => c.id === selectedClient) || {});
  const runningFor = (clientId) => running && confirm?.scope === 'workspace' && confirm?.clientId === clientId;

  const trendData = history.slice(0, 20).reverse().map(h => ({
    date: new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    score: h.llm_visibility_score,
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="geo-report-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Web Search Visibility
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {canRunBulk ? 'How visible each of your workspaces is in web search, check by check.' : 'How visible your site is in web search, check by check.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="run-geo-workspace-btn" onClick={() => openConfirm('workspace', selectedClient)} disabled={!selectedClient || running}
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white disabled:opacity-50">
              <Play size={16} className="mr-2" />
              Run a check{selectedName.display_name || selectedName.name ? ` for ${selectedName.display_name || selectedName.name}` : ''}
            </Button>
            {canRunBulk && (
              <Button data-testid="run-all-geo-btn" variant="outline" onClick={() => openConfirm('all_workspaces')} disabled={running}>
                Run checks for all workspaces
              </Button>
            )}
          </div>
        </div>

        <p className="text-[12.5px] leading-relaxed text-[var(--text-tertiary)] max-w-[80ch]" data-testid="geo-metric-definition">
          How it's measured: Martian runs a fixed set of template web searches about the site through Tavily. A mention in
          Tavily's generated answer counts double; a link among the results counts once. This is a web search signal, not
          AI-assistant answers — for those, see Share of Model and AI Answers &amp; Mentions.
        </p>

        {/* Summary Cards */}
        {report && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="nx-metric-card rounded-lg" data-testid="geo-total-clients">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Workspaces</p>
              <p className="text-2xl font-bold text-[var(--text-primary)] font-mono">{report.total_clients}</p>
            </div>
            <div className="nx-metric-card rounded-lg" data-testid="geo-checked-clients">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Checked</p>
              <p className="text-2xl font-bold text-[var(--accent)] font-mono">{report.checked_clients}</p>
            </div>
            <div className="nx-metric-card rounded-lg" data-testid="geo-avg-score">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Avg web search visibility</p>
              <p className={`text-2xl font-bold font-mono ${report.avg_score >= 50 ? 'text-[var(--success)]' : report.avg_score >= 25 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                {report.avg_score}%
              </p>
            </div>
            <div className="nx-metric-card rounded-lg" data-testid="geo-surge-alerts">
              <p className="text-xs text-[var(--text-tertiary)] font-mono uppercase tracking-wider mb-2">Visibility drops</p>
              <p className={`text-2xl font-bold font-mono ${report.surge_alert_count > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>
                {report.surge_alert_count}
              </p>
            </div>
          </div>
        )}

        {/* Surge Alerts */}
        {report?.surge_alerts?.length > 0 && (
          <div className="nx-metric-card rounded-lg border-[var(--error-border)]" data-testid="surge-alerts-panel">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={18} className="text-[var(--error)]" weight="fill" />
              <h3 className="font-display text-sm font-bold text-[var(--error)]">Visibility Drop Alerts</h3>
            </div>
            {report.surge_alerts.map(a => (
              <div key={a.client_id} className="flex items-center gap-3 p-3 rounded bg-[var(--error-bg)] border border-[var(--error-border)] mb-2">
                <Warning size={16} className="text-[var(--error)] flex-shrink-0" weight="fill" />
                <div className="flex-1">
                  <span className="text-sm text-[var(--text-primary)] font-medium">{a.client_name}</span>
                  <span className="text-xs text-[var(--text-secondary)] ml-2">{a.url}</span>
                </div>
                <span className="text-sm font-mono text-[var(--error)]">{a.score_change > 0 ? '+' : ''}{a.score_change}%</span>
                <span className="text-sm font-mono text-[var(--text-primary)]">{a.llm_visibility_score}%</span>
              </div>
            ))}
          </div>
        )}

        {/* Client Scores Table */}
        <div className="nx-metric-card rounded-lg" data-testid="geo-client-scores">
          <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Web search visibility by workspace</h3>
          <div className="overflow-x-auto">
          <table className="nx-table">
            <thead>
              <tr><th>Workspace</th><th>Website</th><th>Score</th><th>Change</th><th>Last Check</th><th>Action</th></tr>
            </thead>
            <tbody>
              {report?.clients?.map(c => (
                <tr key={c.client_id}>
                  <td className="!text-[var(--text-primary)] font-medium">{c.client_name}</td>
                  <td className="font-mono text-xs">{c.url}</td>
                  <td>
                    {c.llm_visibility_score != null ? (
                      <span className={`font-mono font-bold ${c.llm_visibility_score >= 50 ? 'text-[var(--success)]' : c.llm_visibility_score >= 25 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                        {c.llm_visibility_score}%
                      </span>
                    ) : <span className="nx-tag nx-tag-gray">Not checked</span>}
                  </td>
                  <td>
                    {c.score_change != null ? (
                      <span className={`flex items-center gap-1 font-mono text-sm ${c.score_change > 0 ? 'text-[var(--success)]' : c.score_change < 0 ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>
                        {c.score_change > 0 ? <ArrowUp size={12} /> : c.score_change < 0 ? <ArrowDown size={12} /> : null}
                        {c.score_change > 0 ? '+' : ''}{c.score_change}%
                      </span>
                    ) : <span className="text-[var(--text-tertiary)]">-</span>}
                  </td>
                  <td className="font-mono text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                  <td>
                    <Button size="sm" variant="ghost" data-testid={`run-geo-${c.client_id}`}
                      disabled={running}
                      onClick={() => openConfirm('workspace', c.client_id)}
                      className="text-[var(--accent)] hover:text-[var(--accent-hover)] text-xs">
                      {runningFor(c.client_id) ? <CircleNotch size={12} className="animate-spin" /> : <Play size={12} />}
                      <span className="ml-1">Check</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {/* Historical Trend Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 nx-metric-card rounded-lg" data-testid="geo-trend-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-sm font-bold text-[var(--text-primary)]">
                <ChartLine size={18} className="inline mr-2 text-[var(--accent)]" weight="duotone" />
                Score history
              </h3>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="geo-history-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                  <SelectValue placeholder="Select workspace" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                  {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {trendData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} />
                    <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} axisLine={false} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12 }} />
                    <Line type="monotone" dataKey="score" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <p className="text-[var(--text-tertiary)] text-sm">No checks for this workspace yet.</p>
              </div>
            )}
          </div>

          {/* Recent Checks */}
          <div className="nx-metric-card rounded-lg" data-testid="geo-recent-checks">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">Recent Checks</h3>
            {history.length === 0 ? (
              <p className="text-[var(--text-tertiary)] text-sm text-center py-8">No checks yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.slice(0, 10).map(h => (
                  <div key={h.id} className="p-2 rounded border border-[var(--border-light)] flex items-center gap-3">
                    <span className={`text-lg font-bold font-mono ${h.llm_visibility_score >= 50 ? 'text-[var(--success)]' : h.llm_visibility_score >= 25 ? 'text-[var(--warning)]' : 'text-[var(--error)]'}`}>
                      {h.llm_visibility_score}%
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[var(--text-tertiary)] font-mono">{new Date(h.created_at).toLocaleString()}</p>
                      {h.score_change != null && (
                        <span className={`text-[10px] font-mono ${h.score_change > 0 ? 'text-[var(--success)]' : h.score_change < 0 ? 'text-[var(--error)]' : 'text-[var(--text-tertiary)]'}`}>
                          {h.score_change > 0 ? '+' : ''}{h.score_change}% vs prev
                        </span>
                      )}
                    </div>
                    {h.surge_alert && <Warning size={14} className="text-[var(--error)] flex-shrink-0" weight="fill" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <GeoRunConfirmDialog confirm={confirm} running={running} onCancel={cancelConfirm} onConfirm={runConfirmed} />
    </DashboardLayout>
  );
}
